import {useEffect,useMemo,useRef,useState} from 'react';
import gsap from 'gsap';
import * as THREE from 'three';
import AgentConstellation from './components/AgentConstellation';
import ContextMatrix from './components/ContextMatrix';
import TaskGraph from './components/TaskGraph';
import {
  type AgentRecord,
  type CatalogSnapshot,
  type CatalogStatus,
  type IntentProfileRecord,
  type ToolRecord,
  loadCatalog,
} from './lib/catalog';
import {supabase,supabaseConfigured} from './lib/supabase';

type EventItem={
  id:number;
  text:string;
  tone?:'ok'|'warn'|'info';
};

function NeuralCore(){
 const mountRef=useRef<HTMLDivElement>(null);
 useEffect(()=>{
  const mount=mountRef.current;if(!mount)return;
  const scene=new THREE.Scene();
  scene.fog=new THREE.FogExp2(0x020706,.075);
  const camera=new THREE.PerspectiveCamera(52,mount.clientWidth/mount.clientHeight,.1,100);
  camera.position.z=7.2;
  const renderer=new THREE.WebGLRenderer({antialias:true,alpha:true,powerPreference:'high-performance'});
  renderer.setPixelRatio(Math.min(devicePixelRatio,2));
  renderer.setSize(mount.clientWidth,mount.clientHeight);
  renderer.setClearColor(0x000000,0);
  mount.appendChild(renderer.domElement);

  const root=new THREE.Group();
  scene.add(root);
  const core=new THREE.Mesh(
    new THREE.IcosahedronGeometry(1.22,3),
    new THREE.MeshBasicMaterial({color:0x7fffc1,wireframe:true,transparent:true,opacity:.22})
  );
  const shell=new THREE.Mesh(
    new THREE.IcosahedronGeometry(1.58,2),
    new THREE.MeshBasicMaterial({color:0x66d9ff,wireframe:true,transparent:true,opacity:.075})
  );
  root.add(core,shell);

  const count=6200,pos=new Float32Array(count*3);
  for(let i=0;i<count;i++){
    const r=THREE.MathUtils.randFloat(1.7,4.8);
    const t=Math.random()*Math.PI*2;
    const p=Math.acos(THREE.MathUtils.randFloatSpread(2));
    const j=i*3;
    pos[j]=r*Math.sin(p)*Math.cos(t);
    pos[j+1]=r*Math.sin(p)*Math.sin(t)*.7;
    pos[j+2]=r*Math.cos(p);
  }
  const pg=new THREE.BufferGeometry();
  pg.setAttribute('position',new THREE.BufferAttribute(pos,3));
  const particles=new THREE.Points(
    pg,
    new THREE.PointsMaterial({
      color:0x8bffd0,size:.018,transparent:true,opacity:.56,
      depthWrite:false,blending:THREE.AdditiveBlending
    })
  );
  root.add(particles);

  const starsPos=new Float32Array(1800*3);
  for(let i=0;i<1800;i++){
    const j=i*3;
    starsPos[j]=THREE.MathUtils.randFloatSpread(28);
    starsPos[j+1]=THREE.MathUtils.randFloatSpread(18);
    starsPos[j+2]=THREE.MathUtils.randFloat(-14,3);
  }
  const sg=new THREE.BufferGeometry();
  sg.setAttribute('position',new THREE.BufferAttribute(starsPos,3));
  scene.add(new THREE.Points(
    sg,
    new THREE.PointsMaterial({color:0xb8ffe0,size:.016,transparent:true,opacity:.5})
  ));

  let px=0,py=0;
  const pointer=(e:PointerEvent)=>{
    px=(e.clientX/innerWidth-.5)*2;
    py=(e.clientY/innerHeight-.5)*2;
  };
  addEventListener('pointermove',pointer,{passive:true});

  const clock=new THREE.Clock();
  let frame=0;
  const loop=()=>{
    frame=requestAnimationFrame(loop);
    const t=clock.getElapsedTime();
    root.rotation.y+=.00125;
    particles.rotation.y-=.00045;
    core.rotation.x=t*.085;
    core.rotation.y=t*.11;
    shell.rotation.x=-t*.052;
    shell.rotation.y=t*.064;
    root.position.x+=(px*.18-root.position.x)*.02;
    root.position.y+=(-py*.12-root.position.y)*.02;
    renderer.render(scene,camera);
  };
  loop();

  const resize=()=>{
    camera.aspect=mount.clientWidth/mount.clientHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(mount.clientWidth,mount.clientHeight);
    renderer.setPixelRatio(Math.min(devicePixelRatio,2));
  };
  addEventListener('resize',resize,{passive:true});

  return()=>{
    cancelAnimationFrame(frame);
    removeEventListener('resize',resize);
    removeEventListener('pointermove',pointer);
    renderer.dispose();
    mount.removeChild(renderer.domElement);
  };
 },[]);
 return <div className="neural-canvas" ref={mountRef} aria-hidden="true"/>;
}

function statusLabel(status:CatalogStatus){
  if(status==='connected')return 'CONNECTED';
  if(status==='connecting')return 'CONNECTING';
  if(status==='missing_env')return 'CONFIG REQUIRED';
  return 'OFFLINE';
}

export default function App(){
 const [activeIntent,setActiveIntent]=useState<string|null>(null);
 const [catalogStatus,setCatalogStatus]=useState<CatalogStatus>(
   supabaseConfigured?'connecting':'missing_env'
 );
 const [realtimeStatus,setRealtimeStatus]=useState('STANDBY');
 const [catalog,setCatalog]=useState<CatalogSnapshot>({
   platform:null,node:null,agents:[],tools:[],agentTools:[],intents:[]
 });
 const [events,setEvents]=useState<EventItem[]>([
   {id:1,text:'BOOT // NODE INTERFACE INITIALIZED',tone:'info'}
 ]);
 const shell=useRef<HTMLDivElement>(null);
 const eventId=useRef(2);

 const pushEvent=(text:string,tone:EventItem['tone']='info')=>{
   setEvents(current=>[
     ...current,
     {id:eventId.current++,text,tone}
   ].slice(-7));
 };

 useEffect(()=>{
   const c=gsap.context(()=>{
     gsap.timeline({defaults:{ease:'power3.out'}})
       .from('.system-kicker',{opacity:0,y:10,duration:.6})
       .from('.node-title',{opacity:0,y:24,filter:'blur(18px)',duration:1.1},'-=.15')
       .from('.node-subtitle',{opacity:0,y:12,duration:.7},'-=.5')
       .from('.glass-panel',{opacity:0,y:14,stagger:.09,duration:.6},'-=.25');
   },shell);
   return()=>c.revert();
 },[]);

 useEffect(()=>{
   if(!supabaseConfigured||!supabase){
     setCatalogStatus('missing_env');
     pushEvent('DATABASE // HOSTINGER ENVIRONMENT VARIABLES REQUIRED','warn');
     return;
   }

   const client=supabase;
   let active=true;
   setCatalogStatus('connecting');

   loadCatalog()
     .then(snapshot=>{
       if(!active)return;
       setCatalog(snapshot);
       setCatalogStatus('connected');
       pushEvent('DATABASE // SUPABASE CONNECTED','ok');
       if(snapshot.platform){
         pushEvent(`PLATFORM // ${snapshot.platform.name.toUpperCase()} ONLINE`,'ok');
       }
       pushEvent(
         `CATALOG // ${snapshot.agents.length} AGENTS + ${snapshot.tools.length} TOOLS + ${snapshot.intents.length} INTENTS`,
         'ok'
       );
     })
     .catch(error=>{
       if(!active)return;
       console.error('NODE catalog load failed',error);
       setCatalogStatus('error');
       pushEvent('DATABASE // CATALOG LOAD FAILED','warn');
     });

   const channel=client
     .channel('node-001-runtime')
     .subscribe(status=>{
       if(!active)return;
       if(status==='SUBSCRIBED'){
         setRealtimeStatus('ACTIVE');
         pushEvent('REALTIME BUS // ACTIVE','ok');
       }else if(status==='CHANNEL_ERROR'||status==='TIMED_OUT'){
         setRealtimeStatus('DEGRADED');
       }else{
         setRealtimeStatus('CONNECTING');
       }
     });

   return()=>{
     active=false;
     void client.removeChannel(channel);
   };
 },[]);

 const availableAgents=catalog.agents.filter(agent=>agent.status==='available');
 const availableTools=catalog.tools.filter(tool=>tool.status==='available');
 const platformName=catalog.platform?.name??'AiMaTriX.One';
 const platformDomain=catalog.platform?.domain??'aimatrix.one';
 const platformStatus=(catalog.platform?.status??'booting').toUpperCase();
 const nodeName=catalog.node?.name??'NODE // 001';

 const profile=useMemo<IntentProfileRecord|null>(
   ()=>catalog.intents.find(item=>item.slug===activeIntent)??null,
   [catalog.intents,activeIntent]
 );

 const route=useMemo(()=>{
   if(!profile)return {agents:[] as AgentRecord[],tools:[] as ToolRecord[]};
   return {
     agents:catalog.agents.filter(agent=>profile.agent_slugs.includes(agent.slug)),
     tools:catalog.tools.filter(tool=>profile.tool_slugs.includes(tool.slug)),
   };
 },[profile,catalog.agents,catalog.tools]);

 const selectIntent=(next:IntentProfileRecord)=>{
   setActiveIntent(next.slug);
   const routedAgents=catalog.agents.filter(agent=>next.agent_slugs.includes(agent.slug));
   const routedTools=catalog.tools.filter(tool=>next.tool_slugs.includes(tool.slug));
   pushEvent(`INTENT // ${next.label}`,'info');
   pushEvent(
     `AGENT ROUTE // ${routedAgents.map(agent=>agent.name.toUpperCase()).join(' + ')||'PENDING'}`,
     'ok'
   );
   pushEvent(
     `TOOL BUS // ${routedTools.map(tool=>tool.slug.toUpperCase()).join(' + ')||'PENDING'}`,
     'info'
   );
 };

 const telemetry=[
   ['PLATFORM',platformName.toUpperCase()],
   ['PLATFORM STATE',platformStatus],
   ['COGNITIVE NODE',(catalog.node?.status||'booting').toUpperCase()],
   ['AGENTS',catalogStatus==='connected'? `${availableAgents.length} AVAILABLE` : 'STANDBY'],
   ['TOOLS',catalogStatus==='connected'? `${availableTools.length} AVAILABLE` : 'STANDBY'],
   ['INTENT PROFILES',catalogStatus==='connected'? `${catalog.intents.length} LOADED` : 'STANDBY'],
   ['DATABASE',statusLabel(catalogStatus)],
   ['BUILD','001.3']
 ];

 return <main className="app-shell" ref={shell}>
  <NeuralCore/>
  <div className="ambient-grid"/>
  <div className="scan-lines"/>
  <div className="vignette"/>

  <header className="top-rail">
   <div className="platform-rail-brand">{platformName}</div>
   <div className="system-kicker"><span className="pulse-dot"/> {nodeName} · ORCHESTRATION ENVIRONMENT</div>
   <div>DATA // {catalogStatus==='connected'?'SUPABASE':'LOCAL'}</div>
  </header>

  <section className="hero-copy">
   <div className="platform-wordmark">{platformName}</div>
   <div className="micro-label">COGNITIVE ORCHESTRATION PLATFORM // BUILD 001.3</div>
   <h1 className="node-title">NODE <span>//</span> 001</h1>
   <p className="node-subtitle">INTENT → AGENTS → TOOLS → HUMAN CONTROL → ACTION</p>
  </section>

  <aside className="telemetry-panel glass-panel">
   <div className="panel-label">LIVE SYSTEM TELEMETRY</div>
   {telemetry.map(([label,value])=>
     <div className="telemetry-row" key={label}>
       <span>{label}</span><strong>{value}</strong>
     </div>
   )}
  </aside>

  <ContextMatrix
    profile={profile}
    agents={route.agents.length}
    tools={route.tools.length}
    realtimeStatus={realtimeStatus}
    databaseStatus={statusLabel(catalogStatus)}
  />

  <AgentConstellation
    platformName={platformName}
    nodeName={nodeName}
    profile={profile}
    agents={route.agents}
    tools={route.tools}
    agentTools={catalog.agentTools}
  />

  <TaskGraph profile={profile} onEvent={pushEvent}/>

  <section className="event-rail glass-panel" aria-label="NODE event stream">
   <div className="panel-label">EVENT STREAM</div>
   {events.map(event=>
     <div className={`event-line ${event.tone||'info'}`} key={event.id}>
      <span>›</span>{event.text}
     </div>
   )}
  </section>

  <nav className="intent-dock" aria-label="NODE intent modes">
   {catalog.intents.map((item,index)=>
     <button
       className={`intent-button ${activeIntent===item.slug?'active':''}`}
       onClick={()=>selectIntent(item)}
       key={item.id}
     >
      <span>{String(index+1).padStart(2,'0')}</span>{item.label}
     </button>
   )}
  </nav>

  <footer className="bottom-rail">
   <span>DOMAIN // {platformDomain.toUpperCase()}</span>
   <span className="center-status"><i/> {profile?`VECTOR ${profile.label} ACTIVE`:'PLATFORM NERVOUS SYSTEM ONLINE'}</span>
   <span>BUILD // 001.3 · NODE + SUPABASE</span>
  </footer>
 </main>;
}
